<template>
  <component
    :is="!hasOwnLabel && isLabeledByFormItem ? 'span' : 'label'"
    :class="[
      ns.b(),
      ns.m(checkboxSize),
      ns.is('disabled', isDisabled),
      ns.is('icon', Boolean(icon || $slots.icon)),
      ns.is('checked', isChecked),
      ns.is('indeterminate', indeterminate),
      ns.is('plain', plain),
    ]"
    :aria-controls="indeterminate ? controls : null"
    data-test-name="checkbox"
    data-test-variant=""
    data-test-state=""
    @click="onClickRoot"
  >
    <span
      :class="[
        ns.e('input'),
        ns.is('disabled', isDisabled),
        ns.is('checked', isChecked),
        ns.is('indeterminate', indeterminate),
        ns.is('focus', isFocused),
      ]"
      :tabindex="indeterminate ? 0 : undefined"
      :role="indeterminate ? 'checkbox' : undefined"
      :aria-checked="indeterminate ? 'mixed' : undefined"
    >
      <input
        v-if="trueValue || falseValue"
        :id="inputId"
        v-model="model"
        :class="ns.e('original')"
        type="checkbox"
        :aria-hidden="indeterminate ? 'true' : 'false'"
        :name="name"
        :tabindex="tabindex"
        :disabled="isDisabled"
        :true-value="trueValue"
        :false-value="falseValue"
        @change="handleChange"
        @focus="isFocused = true"
        @blur="isFocused = false"
      />
      <input
        v-else
        :id="inputId"
        v-model="model"
        :class="ns.e('original')"
        type="checkbox"
        :aria-hidden="indeterminate ? 'true' : 'false'"
        :disabled="isDisabled"
        :value="value"
        :name="name"
        :tabindex="tabindex"
        @change="handleChange"
        @focus="isFocused = true"
        @blur="isFocused = false"
      />
      <span :class="ns.e('inner')" />
    </span>
    <span
      v-if="hasOwnLabel"
      :class="[
        ns.e('label'),
        ns.is('has-sub', Boolean($slots.sub || sub)),
        ns.is('has-icon', Boolean($slots.icon || icon)),
      ]"
    >
      <b-icon v-if="$slots.icon || icon" :size="40" :class="ns.e('icon')">
        <slot name="icon">
          <component :is="icon" />
        </slot>
      </b-icon>
      <slot />
      <template v-if="!$slots.default">{{ label }}</template>
      <span v-if="$slots.sub || sub" :class="ns.e('sub')">
        <slot name="sub">
          {{ sub }}
        </slot>
      </span>
    </span>
  </component>
</template>

<script lang="ts" setup>
import { useSlots } from 'vue'
import { useNamespace } from '@bigin/hooks'
import BIcon from '@bigin/components/icon'
import { checkboxEmits, checkboxProps } from './checkbox'
import { useCheckbox } from './composables'

defineOptions({
  name: 'BCheckbox',
})

const props = defineProps(checkboxProps)
defineEmits(checkboxEmits)
const slots = useSlots()

const {
  inputId,
  isLabeledByFormItem,
  isChecked,
  isDisabled,
  isFocused,
  checkboxSize,
  hasOwnLabel,
  model,
  handleChange,
  onClickRoot,
} = useCheckbox(props, slots)

const ns = useNamespace('checkbox')
</script>
